import { Injectable, Inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import * as fromGroups from './actions';
import { map, catchError, flatMap, tap, switchMap } from 'rxjs/operators';
import { LoggingService } from '../../shared/services/logging/logging.service';
import { Store, select } from '@ngrx/store';
import * as fromLookups from '../../lookups';
import { GroupsService } from '../groups.service';
import {
    IStatusMessage,
    ResponseStatus,
    DisplayMessageMinimum
} from '../../shared';
import * as fromShared from '../../shared';

@Injectable()
export class GroupsEffects {
    constructor(
        private actions$: Actions,
        private lookups: Store<fromLookups.LookupState>,
        @Inject(GroupsService) private service: GroupsService,
        @Inject(LoggingService) private log: LoggingService
    ) {}

    LoadGroups$ = createEffect(() =>
        this.actions$.pipe(
            ofType<fromGroups.LoadGroups>(fromGroups.ActionTypes.LoadGroups),
            tap(action =>
                this.log.debug({
                    type: 'GroupsEffects:LoadGroups$:filtered',
                    action
                })
            ),
            // , withLatestFrom(this.lookups.pipe(select(fromLookups.getTasksLookup)))
            switchMap(x =>
                this.service.postGroup(x.payload).pipe(
                    map(exp => new fromGroups.SetGroups(exp)),
                    catchError(fromShared.Errors.handleServiceError)
                )
            )
        )
    );

    LoadGroupMembers$ = createEffect(() =>
        this.actions$.pipe(
            ofType<fromGroups.LoadGroupMembers>(
                fromGroups.ActionTypes.LoadGroupMembers
            ),
            tap(action =>
                this.log.debug({
                    type: 'GroupsEffects:LoadGroupMembers$:filtered',
                    action
                })
            ),
            switchMap(x =>
                this.service.postGroupMember(x.payload).pipe(
                    flatMap(mem => [
                        new fromShared.Errors.SetErrors(
                            [
                                ...mem
                                    .filter(
                                        m =>
                                            m.Status.StatusCode >=
                                            DisplayMessageMinimum
                                    )
                                    .map(m => m.Status)
                            ].filter(fromShared.Errors.filterErrors)
                        ),
                        // new fromShared.Errors.RemoveErrors(
                        //     mem
                        //         .filter(
                        //             i =>
                        //                 i.Status.StatusCode ===
                        //                 ResponseStatus.Success
                        //         )
                        //         .map(i => i.GroupID.toString())
                        // ),
                        new fromGroups.SetGroupMembers(mem)
                    ]),
                    catchError(fromShared.Errors.handleServiceError)
                )
            )
        )
    );

    LoadGroupElements$ = createEffect(() =>
        this.actions$.pipe(
            ofType<fromGroups.LoadGroupElements>(
                fromGroups.ActionTypes.LoadGroupElements
            ),
            tap(action =>
                this.log.debug({
                    type: 'GroupsEffects:LoadGroupElements$:filtered',
                    action
                })
            ),
            // , withLatestFrom(this.lookups.pipe(select(fromLookups.getTasksLookup)))
            switchMap(x =>
                this.service.postGroupElement(x.payload).pipe(
                    map(exp => new fromGroups.SetGroupElements(exp)),
                    catchError(fromShared.Errors.handleServiceError)
                )
            )
        )
    );

    LoadGroupTypes$ = createEffect(() =>
        this.actions$.pipe(
            ofType<fromGroups.LoadGroupTypes>(
                fromGroups.ActionTypes.LoadGroupTypes
            ),
            tap(action =>
                this.log.debug({
                    type: 'GroupsEffects:LoadGroupTypess$:filtered',
                    action
                })
            ),
            switchMap(() =>
                this.service.getGroupType().pipe(
                    map(x => new fromGroups.SetGroupTypes(x)),
                    catchError(fromShared.Errors.handleServiceError)
                )
            )
        )
    );

    LoadGroupElementTypes$ = createEffect(() =>
        this.actions$.pipe(
            ofType<fromGroups.LoadGroupElementTypes>(
                fromGroups.ActionTypes.LoadGroupElementTypes
            ),
            tap(action =>
                this.log.debug({
                    type: 'GroupsEffects:LoadGroupElementTypes$:filtered',
                    action
                })
            ),
            switchMap(x =>
                this.service.postGroupElementType(x.payload).pipe(
                    map(exp => new fromGroups.SetGroupElementTypes(exp)),
                    catchError(fromShared.Errors.handleServiceError)
                )
            )
        )
    );

    UpdateGroups$ = createEffect(() =>
        this.actions$.pipe(
            ofType<fromGroups.UpdateGroups>(
                fromGroups.ActionTypes.UpdateGroups
            ),
            tap(x =>
                this.log.verbose({ type: 'GroupsEffects:UpdateGroups$', x })
            ),
            switchMap(a =>
                this.service.putGroup([...a.payload]).pipe(
                    flatMap(resp => [
                        new fromGroups.UpdateGroupsSuccess(resp),
                        new fromShared.Errors.RemoveErrors(
                            resp.Items.filter(
                                x =>
                                    x.Status.StatusCode ===
                                    ResponseStatus.Success
                            ).map(x => x.GroupID.toString())
                        ),
                        new fromShared.Errors.SetErrors(
                            [
                                ...resp.ErrorMessages.map(
                                    x =>
                                        ({
                                            StatusCode: -1,
                                            Message: x,
                                            MsgProcessID: 0,
                                            MsgEntityID: ''
                                        } as IStatusMessage)
                                ),
                                ...resp.Items.filter(
                                    x =>
                                        x &&
                                        x.Status.StatusCode >=
                                            DisplayMessageMinimum
                                ).map(x => x.Status)
                            ].filter(fromShared.Errors.filterErrors)
                        )
                    ]),
                    catchError(fromShared.Errors.handleServiceError)
                )
            )
        )
    );

    UpdateGroupMembers$ = createEffect(() =>
        this.actions$.pipe(
            ofType<fromGroups.UpdateGroupMembers>(
                fromGroups.ActionTypes.UpdateGroupMembers
            ),
            tap(x =>
                this.log.verbose({
                    type: 'GroupsEffects:UpdateGroupMembers$',
                    x
                })
            ),
            switchMap(a =>
                this.service.putGroupMember([...a.payload]).pipe(
                    flatMap(resp => [
                        new fromGroups.UpdateGroupMembersSuccess(resp),
                        new fromShared.Errors.RemoveErrors(
                            resp.Items.filter(
                                x =>
                                    x.Status.StatusCode ===
                                    ResponseStatus.Success
                            ).map(x => x.ListID.toString())
                        ),
                        new fromShared.Errors.SetErrors(
                            [
                                ...resp.ErrorMessages.map(
                                    x =>
                                        ({
                                            StatusCode: -1,
                                            Message: x,
                                            MsgProcessID: 0,
                                            MsgEntityID: ''
                                        } as IStatusMessage)
                                ),
                                ...resp.Items.filter(
                                    x =>
                                        x &&
                                        x.Status.StatusCode >=
                                            DisplayMessageMinimum
                                ).map(x => x.Status)
                            ].filter(fromShared.Errors.filterErrors)
                        )
                        //new fromGroups.LoadGroupMembers({GroupID: a.payload[0].GroupID})
                    ]),
                    catchError(fromShared.Errors.handleServiceError)
                )
            )
        )
    );

    UpdateGroupElements$ = createEffect(() =>
        this.actions$.pipe(
            ofType<fromGroups.UpdateGroupElements>(
                fromGroups.ActionTypes.UpdateGroupElements
            ),
            tap(x =>
                this.log.verbose({
                    type: 'GroupsEffects:UpdateGroupElements$',
                    x
                })
            ),
            switchMap(a =>
                this.service.putGroupElement([...a.payload]).pipe(
                    flatMap(resp => [
                        new fromGroups.UpdateGroupElementsSuccess(resp),
                        new fromShared.Errors.RemoveErrors(
                            resp.Items.filter(
                                x =>
                                    x.Status.StatusCode ===
                                    ResponseStatus.Success
                            ).map(x => x.GroupElementID.toString())
                        ),
                        new fromShared.Errors.SetErrors(
                            [
                                ...resp.ErrorMessages.map(
                                    x =>
                                        ({
                                            StatusCode: -1,
                                            Message: x,
                                            MsgProcessID: 0,
                                            MsgEntityID: ''
                                        } as IStatusMessage)
                                ),
                                ...resp.Items.filter(
                                    x =>
                                        x &&
                                        x.Status.StatusCode >=
                                            DisplayMessageMinimum
                                ).map(x => x.Status)
                            ].filter(fromShared.Errors.filterErrors)
                        )
                        //new fromGroups.LoadGroupElements({GroupID: a.payload[0].GroupID, GroupElementTypeID: a.payload[0].GroupElementTypeID, ListID: a.payload[0].ListID})
                    ]),
                    catchError(fromShared.Errors.handleServiceError)
                )
            )
        )
    );

    DeleteGroup$ = createEffect(() =>
        this.actions$.pipe(
            ofType<fromGroups.DeleteGroup>(fromGroups.ActionTypes.DeleteGroup),
            switchMap(a =>
                this.service.deleteGroup([...a.payload]).pipe(
                    flatMap(resp => [
                        new fromGroups.DeleteGroupSuccess(resp),
                        new fromShared.Errors.RemoveErrors(
                            resp.Items.filter(
                                x =>
                                    x.Status.StatusCode ===
                                    ResponseStatus.Success
                            ).map(x => x.EntityID.toString())
                        ),
                        new fromShared.Errors.SetErrors(
                            [
                                ...resp.ErrorMessages.map(
                                    x =>
                                        ({
                                            StatusCode: -1,
                                            Message: x,
                                            MsgProcessID: 0,
                                            MsgEntityID: ''
                                        } as IStatusMessage)
                                ),
                                ...resp.Items.filter(
                                    x =>
                                        x &&
                                        x.Status.StatusCode >=
                                            DisplayMessageMinimum
                                ).map(x => x.Status)
                            ].filter(fromShared.Errors.filterErrors)
                        )
                    ]),
                    catchError(fromShared.Errors.handleServiceError)
                )
            )
        )
    );
}
